<template>
  <article class="br-doc-item">
    <div class="br-doc-item__header">
      <div class="br-doc-item__header-names">
        <span v-if="isCurrentDisplay" class="br-doc-item__header-label">
          {{ $t('lbl_EMPBE_BR_List_CurrentCompanyDisplayName') }}
        </span>
        <p v-if="itemDisplayNameLocal" class="br-doc-item__header-name">{{ itemDisplayNameLocal }}</p>
        <p v-if="itemDisplayNameEn" class="br-doc-item__header-name">{{ itemDisplayNameEn }}</p>
        <p
          v-if="!itemDisplayNameLocal && !itemDisplayNameEn"
          class="br-doc-item__header-name"
        >—</p>
      </div>
      <div v-if="showHeaderActions" class="br-doc-item__header-actions">
        <button
          v-if="showEditDisplayName"
          type="button"
          class="br-doc-item__set-current"
          @click.stop="$emit('edit-name', item)"
        >
          {{ $t('lbl_EMPBE_BR_List_EditDisplayName') }}
        </button>
        <button
          v-else-if="showSetAsCurrent"
          type="button"
          class="br-doc-item__set-current"
          @click="$emit('set-as-current', item)"
        >
          {{ $t('lbl_EMPBE_BR_List_SetAsCurrent') }}
        </button>
      </div>
    </div>

    <div class="br-doc-item__divider"></div>

    <div class="br-doc-item__body">
      <!-- 管理端编辑模式：右上角 ⋯ → Invalidate（Figma 2813:66241） -->
      <div
        v-if="showInvalidateMenu"
        class="br-doc-item__admin-menu"
      >
        <button
          type="button"
          class="br-doc-item__admin-menu-btn"
          :aria-label="$t('lbl_EMPBE_BR_Invalidate')"
          :aria-expanded="invalidateMenuOpen ? 'true' : 'false'"
          @click.stop="toggleInvalidateMenu"
        >
          <img
            :src="moreHorizIconUrl"
            class="br-doc-item__admin-menu-icon"
            width="23"
            height="23"
            alt=""
          />
        </button>
        <div
          v-if="invalidateMenuOpen"
          class="br-doc-item__admin-menu-dropdown"
          role="menu"
        >
          <button
            type="button"
            class="br-doc-item__admin-menu-item br-doc-item__admin-menu-item--danger"
            role="menuitem"
            @click.stop="onInvalidateClick"
          >
            {{ $t('lbl_EMPBE_BR_Invalidate') }}
          </button>
        </div>
      </div>

      <button
        type="button"
        class="br-doc-item__thumb"
        :class="{ 'br-doc-item__thumb--pdf': isPdfThumb }"
        :aria-label="$t('lbl_EMPBE_BR_DocumentPreview')"
        @click="$emit('preview', item)"
      >
        <div class="br-doc-item__thumb-inner">
          <img
            v-if="thumbUrl && isImageThumb"
            :src="thumbUrl"
            alt=""
            class="br-doc-item__thumb-img"
            @error="thumbLoadFailed = true"
          />
          <div v-else-if="isPdfThumb" class="br-doc-item__thumb-pdf">
            <i class="fa fa-file-pdf-o br-doc-item__thumb-pdf-icon" aria-hidden="true" />
            <span class="br-doc-item__thumb-pdf-name">{{ pdfDisplayName }}</span>
          </div>
          <div v-else class="br-doc-item__thumb-placeholder">
            <feather type="file-text" :size="28" />
          </div>
        </div>
        <span
          v-if="thumbUrl && (isPdfThumb || (isImageThumb && !thumbLoadFailed))"
          class="br-doc-item__zoom"
          aria-hidden="true"
        >
          <feather type="zoom-in" :size="24" />
        </span>
      </button>

      <div class="br-doc-item__fields">
        <div class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_Status') }}</span>
          <div class="br-doc-item__field-value br-doc-item__field-value--status">
            <BrStatusBadge
              :status-code="badgeStatus"
              :status-name="statusName"
              :label-key="badgeLabelKey"
              :appearance="variant"
            />
          </div>
        </div>

        <div class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_RegisteredName') }}</span>
          <p class="br-doc-item__field-value">{{ companyName }}</p>
        </div>

        <div class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_RegistrationNo') }}</span>
          <p class="br-doc-item__field-value">{{ registrationNo }}</p>
        </div>

        <div v-if="validationText" class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_Validation') }}</span>
          <p class="br-doc-item__field-value">{{ validationText }}</p>
        </div>

        <div class="br-doc-item__field br-doc-item__field--submitted">
          <div class="br-doc-item__field-main">
            <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_SubmittedOn') }}</span>
            <p class="br-doc-item__field-value">{{ submittedOnText || '—' }}</p>
          </div>
          <span v-if="documentId" class="br-doc-item__id">{{ documentId }}</span>
        </div>
      </div>
    </div>

    <!-- 更名待审：来源已生效照提示（Figma Info） -->
    <div v-if="showNameChangeSourcePendingHint" class="br-doc-item__info">
      <img
        :src="infoIconUrl"
        class="br-doc-item__info-icon"
        width="20"
        height="20"
        alt=""
      />
      <p
        class="br-doc-item__info-message"
        v-html="$t('lbl_EMPBE_BR_NameChange_SourceUnderReviewBanner')"
      ></p>
    </div>

    <!-- 供应商：卡片内驳回原因 + 操作 -->
    <BrRejectBanner
      v-if="showRejectedBanner"
      :reject-message="rejectMessage"
      @contact="$emit('contact', item)"
      @reupload="$emit('reupload', item)"
      @cancel="onRejectDeleteClick"
    />
    <!-- 管理端底栏：Reject / Proceed（Figma 已驳回无 Cancel / Re-Upload） -->
    <div v-if="isApproval && hasAdminFooter" class="br-doc-item__admin-footer">
      <slot name="admin-footer" />
    </div>

    <BrExpirySoonBar v-if="isExpiredSoon" />
  </article>
</template>

<script>
import BrStatusBadge from './BrStatusBadge.vue';
import BrRejectBanner from './BrRejectBanner.vue';
import BrExpirySoonBar from './BrExpirySoonBar.vue';
import {
  BR_STATUS,
  formatBrDocumentId,
  formatBrItemValidityText,
  resolveBrItemCompanyName,
  resolveBrItemRegistrationNo,
  resolveBrItemSubmittedOnText,
  brDisplayNamesMatchCompany,
  getItemStatusCode,
  resolveBrBadgeStatus,
  resolveBrDisplayStatus,
  resolveBrRejectReasonText,
  resolveBrUiStatus,
  resolveBrAdminStatusLabelKey,
  shouldShowBrListExpiredSoonBar,
  isBrVerifiedItem,
  canBrSetAsCurrentItem,
  canBrAdminInvalidateItem,
} from '../utils/brDocument';
import { brRuntimeState } from '../../../constants/businessRegistration';
import { BR_LICENSE_ASSETS } from '../../../constants/brLicenseAssets';

export default {
  name: 'BrDocumentListItem',
  components: { BrStatusBadge, BrRejectBanner, BrExpirySoonBar },
  props: {
    item: {
      type: Object,
      required: true,
    },
    displayNameEn: {
      type: String,
      default: '',
    },
    displayNameLocal: {
      type: String,
      default: '',
    },
    thumbUrl: {
      type: String,
      default: '',
    },
    /** supplier | approval | edit */
    variant: {
      type: String,
      default: 'supplier',
      validator: (value) => ['supplier', 'edit', 'approval'].includes(value),
    },
    /** 是否为「Current Display」区主导照 */
    isCurrentDisplay: {
      type: Boolean,
      default: false,
    },
    /** 是否允许展示「Edit Display Name」（页面层判断） */
    canEditName: {
      type: Boolean,
      default: false,
    },
    hasAdminFooter: {
      type: Boolean,
      default: false,
    },
    /** 管理端临期判断用目标司列表；空则回退供应商 runtime documentList */
    expiryContextList: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      thumbLoadFailed: false,
      invalidateMenuOpen: false,
    };
  },
  computed: {
    isApproval() {
      return this.variant === 'approval';
    },
    isEdit() {
      return this.variant === 'edit';
    },
    infoIconUrl() {
      return BR_LICENSE_ASSETS.infoIcon;
    },
    moreHorizIconUrl() {
      return BR_LICENSE_ASSETS.moreHorizIcon;
    },
    /** 编辑模式 + 允许作废的状态才显示 ⋯ */
    showInvalidateMenu() {
      return this.isEdit && canBrAdminInvalidateItem(this.item);
    },
    displayStatus() {
      return this.isApproval
        ? resolveBrUiStatus(this.item)
        : resolveBrDisplayStatus(this.item);
    },
    badgeStatus() {
      return this.isApproval
        ? resolveBrUiStatus(this.item)
        : resolveBrBadgeStatus(this.item);
    },
    statusName() {
      return this.item?.statusName || this.item?.StatusName || '';
    },
    /**
     * 上区（当前生效展示）：已审批及已生效
     * 下区（其他）已通过但未作当前展示：已审批
     * 待生效：已审批 (待生效)（走默认 Status_NotYetEffective）
     */
    badgeLabelKey() {
      const code = String(this.badgeStatus || '').toUpperCase();
      if (this.isApproval && (code === BR_STATUS.PENDING || code === BR_STATUS.PENDING_MANUAL)) {
        return resolveBrAdminStatusLabelKey(code);
      }
      if (code === BR_STATUS.APPROVED || code === BR_STATUS.READY) {
        return this.isCurrentDisplay
          ? 'lbl_EMPBE_BR_Status_Approved'
          : 'lbl_EMPBE_BR_Status_ApprovedOnly';
      }
      return '';
    },
    isRejected() {
      return this.displayStatus === BR_STATUS.REJECTED
        || getItemStatusCode(this.item) === BR_STATUS.REJECTED;
    },
    showRejectedBanner() {
      // 供应商驳回卡：Figma 1142:37555 — 粉横幅 + Add Document + Dismiss/取消
      return (this.variant === 'edit' || this.variant === 'supplier') && this.isRejected;
    },
    isApprovedEffective() {
      return isBrVerifiedItem(this.item);
    },
    /** 本卡 DisplayName 是否已等于当前网上展台名 */
    matchesCurrentCompanyDisplayName() {
      return brDisplayNamesMatchCompany(
        this.item,
        this.displayNameEn,
        this.displayNameLocal,
      );
    },
    showSetAsCurrent() {
      // 审批模式只审阅：不展示「设为当前」（更名钮本就已藏）
      if (this.isApproval) {
        return false;
      }
      // 更名「申请」合成卡不可设为当前；更名通过后的结果主档是真实执照，应可设
      if (this.isCurrentDisplay || this.isNameChangeRequestCard) {
        return false;
      }
      // 已生效未过期，或更名 Skip（NotSelectedAsCompanyName）可手工选用；当前展示卡不显示此钮
      if (!canBrSetAsCurrentItem(this.item)) {
        return false;
      }
      return !this.matchesCurrentCompanyDisplayName;
    },
    /** 任务：仅「已审批及已生效」可更名；Current Display 卡用文字钮替代铅笔 */
    showEditDisplayName() {
      if (this.isApproval || !this.isCurrentDisplay) {
        return false;
      }
      if (!this.canEditName || this.isNameChangeRequestCard) {
        return false;
      }
      return this.isApprovedEffective;
    },
    showHeaderActions() {
      return this.showEditDisplayName || this.showSetAsCurrent;
    },
    isExpiredSoon() {
      // 产品：临期黄条仅「当前公司展示名称」那张已生效 BR
      if (!this.isCurrentDisplay) {
        return false;
      }
      const context = Array.isArray(this.expiryContextList) && this.expiryContextList.length
        ? this.expiryContextList
        : brRuntimeState.documentList;
      return shouldShowBrListExpiredSoonBar(this.item, context, { isCurrentDisplay: true });
    },
    /**
     * 原 BR 更名蓝条：仅当仍有「人工审批中」的更名申请挂在此来源上时显示。
     * 审批通过 / 驳回 / 来源已失效后必须消失。
     */
    showNameChangeSourcePendingHint() {
      const marked = !!(
        this.item?.isSourceOfPendingNameChange
        || this.item?.IsSourceOfPendingNameChange
      );
      if (!marked) {
        return false;
      }
      const displayStatus = resolveBrDisplayStatus(this.item);
      if (
        displayStatus === BR_STATUS.INVALID
        || displayStatus === BR_STATUS.REJECTED
        || displayStatus === BR_STATUS.CANCELLED
      ) {
        return false;
      }
      return true;
    },
    /** 更名申请合成卡（待审/驳回）；不含结果照 */
    isNameChangeRequestCard() {
      return !!(
        this.item?.isNameChangeRequestCard
        || this.item?.IsNameChangeRequestCard
      );
    },
    documentId() {
      return formatBrDocumentId(this.item);
    },
    companyName() {
      return resolveBrItemCompanyName(this.item);
    },
    registrationNo() {
      return resolveBrItemRegistrationNo(this.item);
    },
    validationText() {
      return formatBrItemValidityText(this.item, this.$t.bind(this));
    },
    submittedOnText() {
      return resolveBrItemSubmittedOnText(this.item);
    },
    itemDisplayNameEn() {
      return (
        this.item?.displayNameEn
        || this.item?.DisplayNameEn
        || this.displayNameEn
        || ''
      ).trim();
    },
    itemDisplayNameLocal() {
      const fromItem = (this.item?.displayNameZh || this.item?.DisplayNameZh || '').trim();
      if (fromItem) {
        return fromItem;
      }
      return (this.displayNameLocal || '').trim();
    },
    isPdfThumb() {
      return !!this.thumbUrl && /\.pdf($|\?)/i.test(this.thumbUrl);
    },
    isImageThumb() {
      return !!this.thumbUrl && !this.isPdfThumb;
    },
    /** 优先原文件名；无则回退 OSS 路径末段（历史数据可能是哈希名） */
    pdfDisplayName() {
      const original = String(
        this.item?.originalFileName
        || this.item?.OriginalFileName
        || '',
      ).trim();
      if (original) {
        return original;
      }
      const raw = String(
        this.item?.mediaFileName
        || this.item?.MediaFileName
        || this.thumbUrl
        || '',
      ).trim();
      if (!raw) {
        return 'PDF';
      }
      try {
        const path = /^(https?:)?\/\//i.test(raw) ? new URL(raw, window.location.origin).pathname : raw;
        const base = decodeURIComponent((path.split('/').pop() || '').split('?')[0]);
        return base || 'PDF';
      } catch (e) {
        const base = raw.split('/').pop() || '';
        return base.split('?')[0] || 'PDF';
      }
    },
    rejectMessage() {
      return resolveBrRejectReasonText(this.item, this.$t.bind(this));
    },
  },
  watch: {
    thumbUrl() {
      this.thumbLoadFailed = false;
    },
    invalidateMenuOpen(open) {
      if (open) {
        this.$nextTick(() => {
          document.addEventListener('click', this.onInvalidateMenuDocClick, true);
        });
      } else {
        document.removeEventListener('click', this.onInvalidateMenuDocClick, true);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onInvalidateMenuDocClick, true);
  },
  methods: {
    onRejectDeleteClick() {
      this.$emit('cancel', this.item);
    },
    toggleInvalidateMenu() {
      this.invalidateMenuOpen = !this.invalidateMenuOpen;
    },
    closeInvalidateMenu() {
      this.invalidateMenuOpen = false;
    },
    onInvalidateMenuDocClick(event) {
      const root = this.$el?.querySelector?.('.br-doc-item__admin-menu');
      if (root && root.contains(event.target)) {
        return;
      }
      this.closeInvalidateMenu();
    },
    onInvalidateClick() {
      this.invalidateMenuOpen = false;
      this.$emit('invalidate', this.item);
    },
  },
};
</script>
